/**
 * Local Storage Management
 * Speichert und verwaltet numerologische Profile und Analysen
 */

export interface StoredProfile {
  id: string
  name: string
  dateOfBirth: string
  savedAt: string
  lifePathNumber: number
  destinyNumber: number
  soulUrgeNumber: number
  expressionNumber: number
  birthdayNumber: number
  pushEnabled?: boolean
  preferredHourLocal?: number
  receiveAnitaUpdates?: boolean
  /** Gewähltes Avatar-Emoji (Meditierende Figur, m/w/neutral × Hautton). */
  avatar?: string
}

export interface DailyAnalysis {
  id: string
  date: string
  dailyNumber: number
  savedAt: string
}

const STORAGE_KEYS = {
  PROFILES: 'zahlio_profiles',
  DAILY_ANALYSES: 'zahlio_daily_analyses',
  LAST_PROFILE: 'zahlio_last_profile',
} as const

const LEGACY_STORAGE_KEYS = {
  PROFILES: 'nummi_profiles',
  DAILY_ANALYSES: 'nummi_daily_analyses',
  LAST_PROFILE: 'nummi_last_profile',
} as const

function getStorageValue(primaryKey: string, legacyKey?: string): string | null {
  const primary = localStorage.getItem(primaryKey)
  if (primary !== null) return primary

  if (!legacyKey) return null

  const legacy = localStorage.getItem(legacyKey)
  if (legacy !== null) {
    localStorage.setItem(primaryKey, legacy)
  }
  return legacy
}

export function saveProfile(
  name: string,
  dateOfBirth: string,
  numbers: {
    lifePathNumber: number
    destinyNumber: number
    soulUrgeNumber: number
    expressionNumber: number
    birthdayNumber: number
  }
): StoredProfile {
  const profile: StoredProfile = {
    id: generateId(),
    name,
    dateOfBirth,
    savedAt: new Date().toISOString(),
    ...numbers,
  }

  const profiles = getAllProfiles()
  profiles.push(profile)

  try {
    localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles))
    localStorage.setItem(STORAGE_KEYS.LAST_PROFILE, JSON.stringify(profile))
  } catch (error) {
    console.error('Failed to save profile:', error)
  }

  return profile
}

export function getAllProfiles(): StoredProfile[] {
  try {
    const stored = getStorageValue(STORAGE_KEYS.PROFILES, LEGACY_STORAGE_KEYS.PROFILES)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to load profiles:', error)
    return []
  }
}

export function getLastProfile(): StoredProfile | null {
  try {
    const stored = getStorageValue(STORAGE_KEYS.LAST_PROFILE, LEGACY_STORAGE_KEYS.LAST_PROFILE)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.error('Failed to load last profile:', error)
    return null
  }
}

export function getProfile(id: string): StoredProfile | undefined {
  return getAllProfiles().find((p) => p.id === id)
}

export function updateAvatar(avatar: string): StoredProfile | null {
  const current = getLastProfile()
  if (!current) return null

  const updated: StoredProfile = { ...current, avatar }

  const profiles = getAllProfiles().map((p) => (p.id === updated.id ? updated : p))

  try {
    localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles))
    localStorage.setItem(STORAGE_KEYS.LAST_PROFILE, JSON.stringify(updated))
  } catch (error) {
    console.error('Failed to update avatar:', error)
  }

  return updated
}

export function deleteProfile(id: string): void {
  const profiles = getAllProfiles().filter((p) => p.id !== id)
  try {
    localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles))
  } catch (error) {
    console.error('Failed to delete profile:', error)
  }
}

export function updatePushSettings(enabled: boolean, preferredHourLocal?: number): StoredProfile | null {
  const current = getLastProfile()
  if (!current) return null

  const updated: StoredProfile = {
    ...current,
    pushEnabled: enabled,
    preferredHourLocal: enabled ? preferredHourLocal ?? current.preferredHourLocal ?? 8 : current.preferredHourLocal,
  }

  const profiles = getAllProfiles().map((p) => (p.id === updated.id ? updated : p))

  try {
    localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles))
    localStorage.setItem(STORAGE_KEYS.LAST_PROFILE, JSON.stringify(updated))
  } catch (error) {
    console.error('Failed to update push settings:', error)
  }

  return updated
}

export function updateAnitaUpdatesPreference(receiveAnitaUpdates: boolean): StoredProfile | null {
  const current = getLastProfile()
  if (!current) return null

  const updated: StoredProfile = {
    ...current,
    receiveAnitaUpdates,
  }

  const profiles = getAllProfiles().map((p) => (p.id === updated.id ? updated : p))

  try {
    localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles))
    localStorage.setItem(STORAGE_KEYS.LAST_PROFILE, JSON.stringify(updated))
  } catch (error) {
    console.error('Failed to update Anita updates preference:', error)
  }

  return updated
}

export function clearAllProfiles(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROFILES)
    localStorage.removeItem(STORAGE_KEYS.LAST_PROFILE)
  } catch (error) {
    console.error('Failed to clear profiles:', error)
  }
}

export function saveDailyAnalysis(date: string, dailyNumber: number): DailyAnalysis {
  const analysis: DailyAnalysis = {
    id: generateId(),
    date,
    dailyNumber,
    savedAt: new Date().toISOString(),
  }

  const analyses = getAllDailyAnalyses()
  analyses.unshift(analysis)

  if (analyses.length > 20) {
    analyses.pop()
  }

  try {
    localStorage.setItem(STORAGE_KEYS.DAILY_ANALYSES, JSON.stringify(analyses))
  } catch (error) {
    console.error('Failed to save daily analysis:', error)
  }

  return analysis
}

export function getAllDailyAnalyses(): DailyAnalysis[] {
  try {
    const stored = getStorageValue(STORAGE_KEYS.DAILY_ANALYSES, LEGACY_STORAGE_KEYS.DAILY_ANALYSES)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to load daily analyses:', error)
    return []
  }
}

export function deleteDailyAnalysis(id: string): void {
  const analyses = getAllDailyAnalyses().filter((a) => a.id !== id)
  try {
    localStorage.setItem(STORAGE_KEYS.DAILY_ANALYSES, JSON.stringify(analyses))
  } catch (error) {
    console.error('Failed to delete daily analysis:', error)
  }
}

export function clearAllDailyAnalyses(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.DAILY_ANALYSES)
  } catch (error) {
    console.error('Failed to clear daily analyses:', error)
  }
}

function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('de-CH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Gerade eben'
  if (diffMins < 60) return `vor ${diffMins}m`
  if (diffHours < 24) return `vor ${diffHours}h`
  if (diffDays < 7) return `vor ${diffDays}d`
  return formatDate(date)
}

export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

export default {
  saveProfile,
  getAllProfiles,
  getLastProfile,
  getProfile,
  deleteProfile,
  updatePushSettings,
  updateAnitaUpdatesPreference,
  updateAvatar,
  clearAllProfiles,
  saveDailyAnalysis,
  getAllDailyAnalyses,
  deleteDailyAnalysis,
  clearAllDailyAnalyses,
  isLocalStorageAvailable,
  formatDate,
  formatRelativeTime,
}
